import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';

import { Icon } from '../Icon';
import styles from './Field.module.scss';

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  readonly label?: ReactNode;
  readonly hint?: ReactNode;
  readonly error?: ReactNode;
  readonly required?: boolean;
  readonly wrapperClassName?: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, className, wrapperClassName, id, children, ...rest },
  ref,
) {
  const controlClass = [
    styles.control,
    styles.select,
    error ? styles.invalid : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const control = (
    <div className={styles.inputGroup}>
      <select ref={ref} id={id} className={controlClass} required={required} {...rest}>
        {children}
      </select>
      <span className={styles.trailingIcon}>
        <Icon name="chevron-down" size={16} />
      </span>
    </div>
  );

  if (!label && !hint && !error) {
    return control;
  }

  return (
    <div className={[styles.field, wrapperClassName ?? ''].join(' ')}>
      {label ? (
        <div className={styles.labelRow}>
          <label className={styles.label} htmlFor={id}>
            {label}
          </label>
          {required ? (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          ) : null}
        </div>
      ) : null}
      {control}
      {hint && !error ? <span className={styles.hint}>{hint}</span> : null}
      {error ? <span className={styles.error}>{error}</span> : null}
    </div>
  );
});
